/**
 * The land as a height field.
 *
 * A layout's islands (`islands.ts`) are authoring seeds: an ellipse says
 * roughly where an island is and how big. What is actually rock is this
 * field — the ellipse pulled about by two layers of domain-warping noise so
 * the coast has bays, spits and headlands, a ridged interior so the slopes
 * are not one dome, and a shore that is exactly where the field crosses
 * zero. Server and client build the same field from the same seeds with the
 * same integer-hash noise, so the beach a hull grounds on is the beach that
 * is drawn, to the metre, with nothing on the wire.
 *
 * ── The grid ────────────────────────────────────────────────────────────────
 *
 * The field is sampled once per layout onto a `TERRAIN_CELL`-metre grid over
 * the whole arena (351 × 351 at 20 m) and read back bilinearly. That is what
 * keeps a tick cheap: a hull's grounding is five reads, a line of sight is a
 * read every 25 m, and none of them touch the noise again. The grid is built
 * lazily the first time a layout is asked about and kept for the process's
 * life; at 20 m it is half a megabyte a chart. Twenty metres is under a
 * destroyer's beam, so a coast the grid cannot resolve is one a hull cannot
 * hide in either.
 *
 * ── What a hull is ──────────────────────────────────────────────────────────
 *
 * Everything here treats a hull as a point with a `margin` of beam around it,
 * read as the highest land within that margin (`heightAround`): the same
 * sample the grounding, the bots' course-checking and the tests all use, so
 * the water a bot thinks is clear is the water the arena will not ground it
 * in.
 *
 * Pure and allocation-free after the build. Literal `3500` for the arena
 * half-size because `simulation.ts` imports this file through `islands.ts`;
 * `terrain.test` ties it to `ARENA_HALF`.
 */
import type { Island, IslandLayout } from './islands';

/** Metres between grid samples. */
export const TERRAIN_CELL = 20;
/** Half-size of the field, metres: the arena (`ARENA_HALF`). */
export const TERRAIN_HALF = 3500;
/** Metres between samples along a line of sight. */
export const LOS_STEP = 25;
/** Height of the eye and of the target over the sea for line of sight, metres. */
export const EYE_HEIGHT = 14;

// ─── Noise ───────────────────────────────────────────────────────────────────

/** Value noise on an integer lattice, smoothly interpolated. Deterministic. */
function hash(ix: number, iz: number): number {
  let h = Math.imul(ix, 374761393) + Math.imul(iz, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function valueNoise(x: number, z: number): number {
  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = x - ix;
  const fz = z - iz;
  const sx = fx * fx * (3 - 2 * fx);
  const sz = fz * fz * (3 - 2 * fz);
  const a = hash(ix, iz);
  const b = hash(ix + 1, iz);
  const c = hash(ix, iz + 1);
  const d = hash(ix + 1, iz + 1);
  return a + (b - a) * sx + (c - a) * sz + (a - b - c + d) * sx * sz;
}

/** Fractal noise in [0, 1]. */
export function fbm(x: number, z: number, octaves = 4): number {
  let sum = 0;
  let amp = 0.5;
  let norm = 0;
  let f = 1;
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise(x * f + i * 17.3, z * f - i * 9.1) * amp;
    norm += amp;
    amp *= 0.5;
    f *= 2.03;
  }
  return sum / norm;
}

// ─── The shape of an island ──────────────────────────────────────────────────

/** How far the coast wanders from the seed ellipse, as a fraction of its longer axis. */
const WARP_LOW = 0.2;
const WARP_HIGH = 0.06;
/**
 * How much the shore is ragged, in insideness units: a broad noise for bays
 * and headlands, a finer one for the coves in them. Both fade toward the
 * centre so the centre is always land.
 */
const RAG_BROAD = 0.3;
const RAG_FINE = 0.1;
const RAG = RAG_BROAD + RAG_FINE;

/**
 * How far inside a seed ellipse a point is, 0 on the shore, 1 at the centre,
 * negative outside. Duplicated from `islands.ts` (which imports this file).
 */
function insideness(island: Island, x: number, z: number): number {
  const dx = x - island.x;
  const dz = z - island.z;
  const c = Math.cos(island.yaw);
  const s = Math.sin(island.yaw);
  const u = dx * c - dz * s;
  const v = dx * s + dz * c;
  return 1 - Math.sqrt((u * u) / (island.rx * island.rx) + (v * v) / (island.rz * island.rz));
}

/**
 * The height of one island at a point, or 0. Noise coordinates are offset by
 * the island's place, so two seeds of one size are not the same island.
 */
function islandHeight(island: Island, x: number, z: number): number {
  const L = Math.max(island.rx, island.rz);
  // Cheap reject: nothing of this island reaches past a grown ellipse.
  if (insideness(island, x, z) < -(WARP_LOW + WARP_HIGH + RAG) * 1.6) return 0;
  const ox = island.x * 0.013 + island.yaw * 3.1;
  const oz = island.z * 0.013 - island.yaw * 1.7;
  // Domain warp: where on the seed this point counts as being.
  const lowX = (fbm(x * 0.0011 + ox, z * 0.0011 + oz, 3) - 0.5) * 2;
  const lowZ = (fbm(x * 0.0011 + ox + 31.7, z * 0.0011 + oz + 17.3, 3) - 0.5) * 2;
  const highX = (fbm(x * 0.0046 + ox + 5.1, z * 0.0046 + oz + 2.9, 2) - 0.5) * 2;
  const highZ = (fbm(x * 0.0046 + ox + 11.3, z * 0.0046 + oz - 7.7, 2) - 0.5) * 2;
  const wx = (lowX * WARP_LOW + highX * WARP_HIGH) * L;
  const wz = (lowZ * WARP_LOW + highZ * WARP_HIGH) * L;
  let k = insideness(island, x + wx, z + wz);
  if (k <= -RAG) return 0;
  // Rag the shore: bays and headlands from the broad noise, coves from the
  // fine one; both fade toward the centre, so the centre is always land.
  const fade = 1 - Math.max(0, k);
  k -= RAG_BROAD * (fbm(x * 0.0016 + ox + 43, z * 0.0016 + oz + 29, 3) - 0.5) * 2 * fade;
  k -= RAG_FINE * (fbm(x * 0.0045 + ox + 71, z * 0.0045 + oz + 13, 2) - 0.5) * 2 * fade;
  if (k <= 0) return 0;
  const dome = 1 - (1 - k) * (1 - k);
  // A ridged interior: the slopes vary, and the peak is not one point.
  let h = island.height * dome * (0.8 + 0.4 * fbm(x * 0.006 + ox + 3, z * 0.006 + oz + 8, 4));
  // Fine relief, fading at the shore so the coast is the field's zero line.
  h += island.height * 0.1 * (fbm(x * 0.02 + ox + 61, z * 0.02 + oz + 53, 3) - 0.5) * Math.min(1, k * 3);
  return h > 0 ? h : 0;
}

/** The field before the grid: the highest island at a point. */
export function rawHeight(layout: IslandLayout, x: number, z: number): number {
  let h = 0;
  for (const island of layout.islands) {
    const ih = islandHeight(island, x, z);
    if (ih > h) h = ih;
  }
  return h;
}

// ─── The grid ────────────────────────────────────────────────────────────────

export class Terrain {
  /** Samples per side. */
  readonly n: number;
  readonly heights: Float32Array;
  /** False for open water: every read is 0 and the callers short-circuit. */
  readonly any: boolean;
  /** The tallest sample, metres. */
  readonly peak: number;

  constructor(readonly layout: IslandLayout) {
    this.n = Math.round((TERRAIN_HALF * 2) / TERRAIN_CELL) + 1;
    this.heights = new Float32Array(this.n * this.n);
    this.any = layout.islands.length > 0;
    let peak = 0;
    if (this.any) {
      for (let j = 0; j < this.n; j++) {
        const z = -TERRAIN_HALF + j * TERRAIN_CELL;
        for (let i = 0; i < this.n; i++) {
          const h = rawHeight(layout, -TERRAIN_HALF + i * TERRAIN_CELL, z);
          this.heights[j * this.n + i] = h;
          if (h > peak) peak = h;
        }
      }
    }
    this.peak = peak;
  }

  /** Land height at a point, metres above the sea; 0 on water and off the chart. */
  heightAt(x: number, z: number): number {
    if (!this.any) return 0;
    const fx = (x + TERRAIN_HALF) / TERRAIN_CELL;
    const fz = (z + TERRAIN_HALF) / TERRAIN_CELL;
    if (fx < 0 || fz < 0 || fx > this.n - 1 || fz > this.n - 1) return 0;
    const i = Math.min(this.n - 2, Math.floor(fx));
    const j = Math.min(this.n - 2, Math.floor(fz));
    const tx = fx - i;
    const tz = fz - j;
    const h = this.heights;
    const a = h[j * this.n + i]!;
    const b = h[j * this.n + i + 1]!;
    const c = h[(j + 1) * this.n + i]!;
    const d = h[(j + 1) * this.n + i + 1]!;
    return a + (b - a) * tx + (c - a) * tz + (a - b - c + d) * tx * tz;
  }

  /**
   * The highest land within `margin` metres of a point: the point itself and
   * four more at the margin. A hull is this, not a point.
   */
  heightAround(x: number, z: number, margin: number): number {
    if (!this.any) return 0;
    if (margin <= 0) return this.heightAt(x, z);
    let h = this.heightAt(x, z);
    const m = margin * 0.7071;
    h = Math.max(h, this.heightAt(x + m, z + m), this.heightAt(x - m, z + m), this.heightAt(x + m, z - m), this.heightAt(x - m, z - m));
    return h;
  }

  /** True when the disc of `radius` round a point has any land in it. Samples every half cell. */
  landInDisc(x: number, z: number, radius: number): boolean {
    if (!this.any) return false;
    const step = TERRAIN_CELL / 2;
    for (let dz = -radius; dz <= radius; dz += step) {
      const w = Math.sqrt(Math.max(0, radius * radius - dz * dz));
      for (let dx = -w; dx <= w; dx += step) if (this.heightAt(x + dx, z + dz) > 0) return true;
    }
    return false;
  }

  /** Slope of `heightAround` at a point, by central differences one cell wide. */
  gradient(x: number, z: number, margin: number, out: { x: number; z: number }): void {
    const d = TERRAIN_CELL * 0.5;
    out.x = (this.heightAround(x + d, z, margin) - this.heightAround(x - d, z, margin)) / (2 * d);
    out.z = (this.heightAround(x, z + d, margin) - this.heightAround(x, z - d, margin)) / (2 * d);
  }
}

const cache = new WeakMap<IslandLayout, Terrain>();

/** The field for a layout, built on first use and kept. */
export function terrainFor(layout: IslandLayout): Terrain {
  let t = cache.get(layout);
  if (!t) {
    t = new Terrain(layout);
    cache.set(layout, t);
  }
  return t;
}
